import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  ColumnLayout,
  Container,
  ContentLayout,
  DatePicker,
  FormField,
  Header,
  Icon,
  Input,
  Modal,
  Popover,
  Select,
  Slider,
  SpaceBetween,
} from '@cloudscape-design/components';
import { doc, Timestamp, updateDoc, WhereFilterOp } from '@firebase/firestore';

import { v4 } from 'uuid';
import { Agp } from '../api/agp';
import { createBatch, fetchBatch, updateBatch } from '../api/batches';
import { deleteItem, fetchItemsByIds, fetchItemsWithFilter, Filter } from '../api/generic';
import { adjustInventoryQuantity } from '../api/inventory';
import { fetchRecipe } from '../api/recipes';
import { ConfirmIngredientsModal } from '../components/ConfirmIngredientsModal';
import DepartmentSelector from '../components/DepartmentSelector';
import ErrorBar from '../components/ErrorBar';
import ExciseDutyModal from '../components/ExciseDutyModal';
import Flex from '../components/Flex';
import GravitySample from '../components/GravitySampleModal';
import LossModal from '../components/LossModal';
import AgpModal from '../components/modules/AgpModal';
import GridLinkModal from '../components/modules/GridLinkModal';
import HydroPi, { HydroPiSample, iSpindelSample } from '../components/modules/HydroPi';
import HydroPiConfigModal from '../components/modules/HydroPiConfig';
import TasteSampleModal from '../components/TasteSampleModal';
import UpdateQuantityModal from '../components/UpdateQuantityModel';
import { db } from '../config/firebase';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { Batch, getBatchSlug } from '../models/Batch';
import InventoryItem, { RecipeIngredient } from '../models/Inventory';
import { Recipe } from '../models/Recipe';
import { GravitySample as GS, TasteSample } from '../models/Sampling';
import { Settings } from '../models/Settings';
import BatchGraphView from '../modules/BatchGraphView';
import Cost from '../modules/Cost';
import CurrentStep from '../modules/CurrentStep';
import ExciseDuty from '../modules/ExciseDuty';
import Gravity from '../modules/Gravity';
import { Ingredients } from '../modules/Ingredients';
import Steps from '../modules/Steps';
import Taste from '../modules/Taste';
import {
  getAdjustedFinalAbvFromManualSamples,
  getFinalAbvFromHydroPi,
  getFinalAbvFromManualSamples,
} from '../utils/abv';
import { prefixCollection } from '../utils/prefix';
import { sortGravitySamples, sortName } from '../utils/sort';
import { addDaysBySteps, toDate } from '../utils/timestamp';

export const renderStars = (score: number) => {
  const stars = [];
  const starCount = Math.round((score / 100) * 2) / 2; // Round to nearest 0.5
  const fullStars = Math.floor(starCount); // Number of full stars
  const halfStar = starCount % 1 !== 0; // Check if a half star is needed
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0); // Remaining empty stars

  // Add full stars
  for (let i = 0; i < fullStars; i++) {
    stars.push(<Icon key={`full-${i}`} name="star-filled" />);
  }

  // Add half star if applicable
  if (halfStar) {
    stars.push(<Icon key="half" name="star-half" />);
  }

  // Add empty stars
  for (let i = 0; i < emptyStars; i++) {
    stars.push(<Icon key={`empty-${i}`} name="star" />);
  }

  return stars;
};

export const initalBatch: Batch = {
  id: '',
  name: '',
  recipeId: '',
  startDate: new Date(),
  status: 'planned',
  quantity: 0,
  volume: 0,
  yield: 0,
  expectedYield: 0,
  currentStepIndex: 0,
  stepStatus: 'not-started',
};

const BatchPage = () => {
  const { tenant, tenantObject, tier, userObject } = useAuthContext();
  const { id, modal } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState<Batch | null>(null);

  const [batchAgp, setBatchAgp] = useState<Agp[]>([]);
  const [abvFinal, setAbvFinal] = useState<number>();

  const [ingredients, setIngredients] = useState<InventoryItem[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [gravitySamples, setGravitySamples] = useState<GS[]>([]);
  const [notes, setNotes] = useState('');
  const [score, setScore] = useState(-1);

  const [editMode, setEditMode] = useState<boolean>(id === 'new');

  const [quantity, setQuantity] = useState('');
  const [volume, setVolume] = useState('');
  const [yieldAmount, setYieldAmount] = useState('');
  const [expectedYield, setExpectedYield] = useState('');

  const [showHydroPiModal, setShowHydroPiModal] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [editStep, setEditStep] = useState(-1);

  const [finishModal, setFinishModal] = useState(false);
  const [error, setError] = useState<Error>();
  const [settings, setSettings] = useState<Settings>();

  const [showTasteModal, setShowTasteModal] = useState(false);
  const [showExciseDutyModal, setShowExciseDutyModal] = useState(false);
  const [showAgpModal, setShowAgpModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLossModal, setShowLossModal] = useState(false);
  const [showIngredientModal, setShowIngredientModal] = useState(false);

  const [unadjustedAbv, setUnadjustedAbv] = useState<number>();
  const [adjustedAbv, setAdjustedAbv] = useState<number>();

  const [originalOg, setOriginalOg] = useState<number>();
  const [originalFg, setOriginalFg] = useState<number>();
  const [totalSugarGrams, setTotalSugarGrams] = useState<number>();
  const [volumeLiters, setVolumeLiters] = useState<number>();
  const [extraGP, setExtraGP] = useState<number>();
  const [adjustedOg, setAdjustedOg] = useState<number>();
  const [adjustmentIngredients, setAdjustmentIngredients] = useState<
    {
      name: string;
      quantity: number;
      unit: string;
      sugarContentPer100g: number;
      sugarGrams: number;
    }[]
  >([]);

  const [editedIngredients, setEditedIngredients] = useState<RecipeIngredient[]>([]);

  const [hydroPiSamples, setHydroPiSamples] = useState<HydroPiSample[]>([]);

  useSubscribe<Recipe>(prefixCollection('recipes', tenant), setRecipes, setError, sortName);

  const gravityFilters: Filter<GS>[] = id ? [{ field: 'batchId', operator: '==', value: id }] : [];

  useSubscribe<GS>(
    prefixCollection('gravity', tenant),
    setGravitySamples,
    setError,
    sortGravitySamples,
    gravityFilters,
  );

  useSubscribe<Settings>(prefixCollection('settings', tenant), (items) => setSettings(items[0]), setError);

  useEffect(() => {
    if (!batch || !gravitySamples.length) return;

    const hasFruit =
      batch.recipe?.ingredients?.some((i) => (i.item?.sugarContentPer100g ?? 0) > 0) ||
      ingredients?.some((i) => (i?.sugarContentPer100g ?? 0) > 0);

    if (batch.hydroPi?.color) {
      // 🔵 HydroPi / iSpindel batch
      const abv = getFinalAbvFromHydroPi(hydroPiSamples);
      if (abv !== undefined) {
        setUnadjustedAbv(parseFloat(abv.toFixed(2)));
        setAdjustedAbv(parseFloat(abv.toFixed(2))); // HydroPi captures live gravity changes
      }
    } else {
      // 🔵 Manual gravity sample batch
      const needsBackup =
        ingredients?.some((i) => (i?.sugarContentPer100g ?? 0) > 0) &&
        !batch.recipe?.ingredients?.some((i) => (i.item?.sugarContentPer100g ?? 0) > 0);

      const batchToUse = needsBackup
        ? {
            ...batch!,
            recipe: batch?.recipe
              ? {
                  ...batch.recipe,
                  ingredients: batch.recipe.ingredients.map((ingredient) => {
                    const updatedItem = ingredients.find((i) => i.id === ingredient.item.id);
                    return updatedItem ? { ...ingredient, item: updatedItem } : ingredient;
                  }),
                }
              : undefined,
          }
        : batch;

      const rawAbv = getFinalAbvFromManualSamples(gravitySamples);
      const correctedAbv = hasFruit ? getAdjustedFinalAbvFromManualSamples(gravitySamples, batchToUse) : rawAbv;

      if (rawAbv !== undefined) setUnadjustedAbv(parseFloat(rawAbv.toFixed(2)));
      if (correctedAbv !== undefined) setAdjustedAbv(parseFloat(correctedAbv.toFixed(2)));

      // 📣 Save sugar adjustment details if fruit is present
      if (hasFruit && batchToUse.recipe?.ingredients?.length) {
        const sorted = [...gravitySamples].sort((a, b) => toDate(a.date).getTime() - toDate(b.date).getTime());

        const og = parseFloat(sorted[0]?.og);
        const fg = parseFloat(sorted[sorted.length - 1]?.fg);

        if (!og || !fg) return;

        setOriginalOg(og);
        setOriginalFg(fg);

        let totalSugar = 0;

        batchToUse.recipe.ingredients.forEach((ingredient) => {
          if (!ingredient.item?.sugarContentPer100g) return;

          const quantity = ingredient.quantity ?? 0;
          const unit = ingredient.item.unit?.toLowerCase() ?? 'kg';

          let quantityKg = quantity;

          if (unit === 'g' || unit === 'gram' || unit === 'grams') {
            quantityKg = quantity / 1000;
          } else if (unit === 'mg') {
            quantityKg = quantity / 1_000_000;
          }

          const sugarContent = ingredient.item.sugarContentPer100g ?? 0;
          const sugarGrams = quantityKg * 1000 * (sugarContent / 100);

          totalSugar += sugarGrams;
        });

        setTotalSugarGrams(totalSugar);

        const volume = batch.volume ?? batch.quantity ?? 1;
        setVolumeLiters(volume);

        const extraGp = totalSugar / (volume * 10);
        setExtraGP(extraGp);

        const adjOg = og + extraGp / 1000;
        setAdjustedOg(adjOg);

        const adjustmentList: {
          name: string;
          quantity: number;
          unit: string;
          sugarContentPer100g: number;
          sugarGrams: number;
        }[] = [];

        batchToUse.recipe.ingredients.forEach((ingredient) => {
          if (!ingredient.item?.sugarContentPer100g) return;

          const quantity = ingredient.quantity ?? 0;
          const unit = ingredient.item.unit?.toLowerCase() ?? 'kg';

          let quantityKg = quantity;

          if (unit === 'g' || unit === 'gram' || unit === 'grams') {
            quantityKg = quantity / 1000;
          } else if (unit === 'mg') {
            quantityKg = quantity / 1_000_000;
          }

          const sugarContent = ingredient.item.sugarContentPer100g ?? 0;
          const sugarGrams = quantityKg * 1000 * (sugarContent / 100);

          totalSugar += sugarGrams;

          adjustmentList.push({
            name: ingredient.item.name,
            quantity,
            unit,
            sugarContentPer100g: sugarContent,
            sugarGrams: parseFloat(sugarGrams.toFixed(2)),
          });
        });

        setAdjustmentIngredients(adjustmentList);
      }
    }
  }, [batch, gravitySamples, hydroPiSamples, ingredients]);

  const confirmIngredients = async (subtract: boolean) => {
    if (!batch || !batch.recipe) return;

    const updatedRecipe = {
      ...batch.recipe,
      ingredients: editedIngredients, // 🛠️ Use the updated ingredients from modal!
    };

    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, {
      ingredientsConfirmed: true,
      recipe: updatedRecipe,
    });

    if (subtract) {
      await Promise.all(
        editedIngredients.map(async ({ item, quantity }) => {
          const q = Number(quantity);
          // ignore non-finite / non-positive inputs
          if (!Number.isFinite(q) || q <= 0) return;

          await adjustInventoryQuantity(
            item.id, // must be present on the ingredient item
            {
              operation: 'remove', // delta subtraction
              quantity: q,
              action: 'inventory_depletion', // using ingredients -> depletion
              userId: userObject?.id,
              batchId: batch.id,
            },
            tenant,
          );
        }),
      );
    }

    if (id) fetchBatch(id, tenant).then(setBatch);
    setShowIngredientModal(false);
  };

  useEffect(() => {
    if (batch?.recipe?.ingredients) {
      setEditedIngredients(batch.recipe.ingredients.map((ri) => ({ ...ri })));
    }
  }, [showIngredientModal]);

  const fetchInventoryItemsFromRecipe = async () => {
    const invItems = await fetchItemsByIds<InventoryItem>(
      prefixCollection('inventory', tenant),
      batch?.recipe?.ingredients.map((x) => x.item.id) ?? [],
    );
    setIngredients(invItems ?? []);
  };

  useEffect(() => {
    if (!batch || !batch.id) return;

    // Fetch current Inventory
    fetchInventoryItemsFromRecipe();
  }, [batch, tenant]);

  useEffect(() => {
    if (!batch || !batch.id) return;

    // Fetch AGP
    const agpFilter: Filter<Agp> = {
      field: 'batchId',
      operator: '==',
      value: batch.id,
    };
    fetchItemsWithFilter<Agp>(prefixCollection('agp', tenant), [agpFilter]).then(setBatchAgp);

    // Fetch ABV from HydroPi if available
    const fetchAbv = async () => {
      if (!batch.hydroPi || !batch.hydroPi.startDate) return;

      const isTilt = batch.hydroPi.type === 'Tilt';
      const collection = isTilt ? 'hydroPi' : 'ispindel';
      const filters: Filter<any>[] = [
        {
          field: 'color',
          operator: '==',
          value: batch.hydroPi.color,
        },
        {
          field: 'timestamp',
          operator: '>=',
          value: batch.hydroPi.startDate,
        },
      ];
      if (batch.hydroPi.endDate) {
        filters.push({
          field: 'timestamp',
          operator: '<=',
          value: batch.hydroPi.endDate,
        });
      }

      const rawSamples = await fetchItemsWithFilter<any>(prefixCollection(collection, tenant, '-'), filters);

      const samples: HydroPiSample[] = isTilt
        ? rawSamples
        : rawSamples.map((s) => ({
            timestamp: s.timestamp,
            avg_gravity: s.gravity,
            avg_temp: s.temperature ?? 20,
            avg_temp_c: s.temperature ?? 20,
            temp_stddev: 0,
            gravity_stddev: 0,
            color: s.color ?? 'unknown',
            uuid: s.ID?.toString() ?? 'unknown',
            id: '',
          }));

      const abv = getFinalAbvFromHydroPi(samples);
      setAbvFinal(abv);
    };

    fetchAbv();
  }, [batch, tenant]);

  useEffect(() => {
    if (!batch?.hydroPi || !batch?.hydroPi?.color) return;

    const fetchSamples = async () => {
      if (!batch?.hydroPi || !batch?.hydroPi?.color) return;

      const rawSamples = await fetchItemsWithFilter<HydroPiSample | iSpindelSample>(
        prefixCollection(batch.hydroPi?.type === 'iSpindel' ? 'ispindel' : 'hydroPi', tenant, '-'),
        [
          {
            field: 'color' as keyof (HydroPiSample | iSpindelSample),
            operator: '==',
            value: batch.hydroPi.color ?? 'NA',
          },
          {
            field: 'timestamp' as keyof (HydroPiSample | iSpindelSample),
            operator: '>=',
            value: batch.hydroPi?.startDate,
          },
          ...(batch.hydroPi.endDate
            ? [
                {
                  field: 'timestamp' as keyof (HydroPiSample | iSpindelSample),
                  operator: '<=' as WhereFilterOp,
                  value: batch.hydroPi.endDate,
                },
              ]
            : []),
        ],
      );

      const normalizedSamples: HydroPiSample[] =
        batch.hydroPi.type === 'iSpindel'
          ? (rawSamples as iSpindelSample[]).map((s) => ({
              timestamp: s.timestamp,
              avg_gravity: s.gravity,
              avg_temp: s.temperature ?? 20,
              avg_temp_c: s.temperature ?? 20,
              temp_stddev: 0,
              gravity_stddev: 0,
              color: s.color ?? 'unknown',
              uuid: String(s.ID ?? 'unknown'), // make sure uuid is a string
              id: '',
            }))
          : (rawSamples as HydroPiSample[]);

      // Ensure there are at least two samples for meaningful calculations
      if (normalizedSamples.length < 2) {
        console.warn('Not enough samples for meaningful calculations.');
        setHydroPiSamples([]);
        return;
      }

      setHydroPiSamples(normalizedSamples);
    };

    fetchSamples();
  }, [batch, tenant]);

  useEffect(() => {
    if (!editMode && id) {
      if (id === 'new') {
        setBatch(initalBatch);
      } else {
        fetchBatch(id, tenant).then(setBatch);
      }
    }
  }, [editMode, id, tenant]);

  useEffect(() => {
    if (id && id !== 'new') {
      fetchBatch(id, tenant).then((b) => {
        if (!b) return; // Handle potential null/undefined batch safely

        setQuantity(b.quantity?.toString() ?? '');
        setVolume(b.volume?.toString() ?? '');
        setYieldAmount(b.yield?.toString() ?? '');
        setExpectedYield(b.expectedYield?.toString() ?? '');
        setNotes(b.notes ?? '');
        setScore(b.score ?? -1);

        const startDate = (b.startDate as Timestamp)?.toDate()?.toISOString().split('T')[0] ?? '';
        setStartDate(startDate);

        setBatch(b);
      });
    }
  }, [id, tenant]);

  useEffect(() => {
    if (batch && batch.recipeId && !batch.recipe) {
      fetchRecipe(batch.recipeId, tenant).then((r) => {
        if (r) {
          setBatch((prevBatch) => ({
            ...(prevBatch ?? initalBatch),
            recipe: r,
          }));
        }
      });
    }
  }, [batch, tenant]);

  const saveBatch = async () => {
    if (!batch) return;

    if (batch.id === '') {
      const docRef = await createBatch(
        {
          ...batch,
          quantity: parseFloat(quantity),
          volume: parseFloat(volume),
          yield: parseFloat(yieldAmount),
          expectedYield: parseFloat(expectedYield),
          recipeSlug: await getBatchSlug(batch, tenant),
        },
        tenant,
      );
      navigate(`/batches/${docRef.id}`);
    } else {
      const newBatchData = {
        ...batch,
        quantity: parseFloat(quantity),
        volume: parseFloat(volume),
        yield: parseFloat(yieldAmount),
        expectedYield: parseFloat(expectedYield),
        recipeSlug: await getBatchSlug(batch, tenant),
        notes,
        score,
      };
      await updateBatch(batch.id, newBatchData, tenant);
      setBatch(newBatchData);
    }
  };

  const setFinalAbv = async () => {
    if (!batch) return;

    // if no hydroPi, use manual samples (gravitySamples)
    if (!batch.hydroPi) {
      const abv = getFinalAbvFromManualSamples(gravitySamples);
      if (abv) {
        await updateBatch(batch.id, { abvFinal: parseFloat(abv.toFixed(2)) }, tenant);
        if (id) fetchBatch(id, tenant).then(setBatch);
      }
    }
  };

  const handleAddTasteSample = async (sample: Omit<TasteSample, 'id' | 'batchId'>) => {
    const newSample: TasteSample = {
      ...sample,
      id: v4(), // Unique ID for the sample
      batchId: batch?.id || '', // Link to the current batch
    };

    const updatedSamples = [...(batch?.tasteSamples ?? []), newSample];

    // Save updated samples to Firestore (optional)
    if (batch) {
      await updateBatch(batch.id, { ...batch, tasteSamples: updatedSamples }, tenant);
      if (id && id !== 'new') {
        fetchBatch(id, tenant).then(setBatch);
      }
    }
  };

  const cancelEdit = () => {
    setEditMode(false);
    if (id && id !== 'new') {
      fetchBatch(id, tenant).then(setBatch);
    } else {
      navigate(`/batches`);
    }
  };

  const archiveBatch = async () => {
    if (!batch) return;
    const confirm = window.confirm('Are you sure you want to archive this batch?');
    if (!confirm) return;
    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, { status: 'archived' });
    navigate(`/batches`);
  };

  const reActivateBatch = async () => {
    if (!batch) return;
    const confirm = window.confirm('Are you sure you want to reactivate this batch?');
    if (!confirm) return;
    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, { status: 'planned' });
    if (id) fetchBatch(id, tenant).then(setBatch);
  };

  const stepStart = async () => {
    if (!batch) return;
    const newRecipe = batch.recipe;
    if (!newRecipe) return;

    const newStep = newRecipe.steps[batch.currentStepIndex];
    if (!newStep) return;

    newStep.start = new Date();

    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, {
      stepStatus: 'in-progress',
      status: 'in-progress',
      recipe: newRecipe,
    });
    if (id) fetchBatch(id, tenant).then(setBatch);
  };
  const stepComplete = async () => {
    if (!batch) return;

    const newRecipe = batch.recipe;
    if (!newRecipe) return;

    const newStep = newRecipe.steps[batch.currentStepIndex];
    if (!newStep) return;

    newStep.end = new Date();

    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, { stepStatus: 'completed', recipe: newRecipe });
    if (id) fetchBatch(id, tenant).then(setBatch);
  };
  const stepNext = async () => {
    if (!batch) return;
    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, {
      currentStepIndex: batch.currentStepIndex + 1,
      stepStatus: 'not-started',
    });
    if (id) fetchBatch(id, tenant).then(setBatch);
  };

  const stepPrevious = async () => {
    if (!batch) return;
    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);
    await updateDoc(itemRef, {
      currentStepIndex: batch.currentStepIndex - 1,
      stepStatus: 'not-started',
    });
    if (id) fetchBatch(id, tenant).then(setBatch);
  };

  const updateBatchStatus = async (status: 'planned' | 'in-progress' | 'completed' | 'archived') => {
    if (!batch) return;
    const itemRef = doc(db, prefixCollection('batches', tenant), batch.id);

    let abvFinalToSave = abvFinal;

    // Recalculate ABV just in time if completing
    if (status === 'completed' && abvFinalToSave === undefined) {
      if (batch.hydroPi?.color && hydroPiSamples.length) {
        abvFinalToSave = getFinalAbvFromHydroPi(hydroPiSamples);
      } else if (gravitySamples.length) {
        const needsBackup =
          ingredients?.some((i) => (i?.sugarContentPer100g ?? 0) > 0) &&
          !batch.recipe?.ingredients?.some((i) => (i.item?.sugarContentPer100g ?? 0) > 0);

        const batchToUse = needsBackup
          ? {
              ...batch!,
              recipe: {
                ...batch.recipe!,
                ingredients: batch.recipe!.ingredients.map((ingredient) => {
                  const updatedItem = ingredients.find((i) => i.id === ingredient.item.id);
                  return updatedItem ? { ...ingredient, item: updatedItem } : ingredient;
                }),
              },
            }
          : batch;

        abvFinalToSave = batchToUse.recipe?.ingredients?.some((i) => i.item?.sugarContentPer100g)
          ? getAdjustedFinalAbvFromManualSamples(gravitySamples, batchToUse)
          : getFinalAbvFromManualSamples(gravitySamples);
      }
    }

    const updateItem: any = {
      status,
      ...(status === 'completed' && {
        endDate: new Date(),
        abvFinal: abvFinalToSave ?? null,
      }),
    };

    await updateDoc(itemRef, updateItem);
    if (id) fetchBatch(id, tenant).then(setBatch);
  };

  const handleDelete = async () => {
    if (window.confirm('Delete the batch?!') && id) {
      await deleteItem(prefixCollection('batches', tenant), id);
      navigate('/batches');
    }
  };

  const batchSize = (batch?.quantity ?? 1) / (batch?.recipe?.batchSize ?? 25);

  return (
    <>
      <ContentLayout
        disableOverlap
        header={
          <Header
            actions={
              <SpaceBetween size="m" direction="horizontal">
                <Button onClick={() => navigate('/batches')} iconName="angle-left">
                  Back
                </Button>
                <Button
                  variant={editMode ? 'primary' : 'normal'}
                  onClick={() => {
                    if (editMode) {
                      saveBatch();
                    }
                    setEditMode((x) => !x);
                  }}
                  iconName={'edit'}
                >
                  {editMode ? 'Save' : 'Edit batch'}
                </Button>
                <Button onClick={() => navigate('/batches/' + id + '/recipe/recipe')} iconName="edit">
                  Recipe
                </Button>
                <Button onClick={handleDelete} iconName="delete-marker">
                  Delete
                </Button>
                {editMode && <Button onClick={cancelEdit}>Cancel</Button>}
                {id &&
                  id !== 'new' &&
                  (batch?.status !== 'archived' ? (
                    <Button onClick={archiveBatch} iconName="download">
                      Archive
                    </Button>
                  ) : (
                    <Button onClick={reActivateBatch} iconName="upload">
                      Reactivate
                    </Button>
                  ))}
                {settings && settings.integrations.map((x) => x.integrationName).includes('squarespace') && (
                  <Button
                    disabled={!batch?.exciseDuty || !batch?.exciseDuty?.exciseDutyPaid}
                    onClick={() => setFinishModal(true)}
                    iconName="status-positive"
                  >
                    Add to stock
                  </Button>
                )}
              </SpaceBetween>
            }
            variant="h1"
            description={
              id === 'new' ? 'Create new batch' : (batch?.recipe?.name ?? '') + ' - ' + (batch?.recipe?.style ?? '')
            }
          >
            {id === 'new' ? 'Create new batch' : `${batch?.name ?? 'N/A'} (${batch?.batchId ?? 'xx_xx'})`}
          </Header>
        }
      >
        <div style={{ marginTop: 10 }}>
          <ErrorBar error={error} setError={setError} />
          <Container
            header={
              <Header
                actions={
                  <>
                    {batch && tier >= 3 && (
                      <Flex align="center">
                        <Button variant="link" onClick={() => setShowIngredientModal(true)}>
                          <Badge color={batch?.ingredientsConfirmed ? 'green' : 'red'}>
                            {batch?.ingredientsConfirmed ? 'Confirmed' : 'Ingredients not confirmed!'}
                          </Badge>
                        </Button>
                      </Flex>
                    )}
                    {id && id !== 'new' && (
                      <ButtonDropdown
                        onItemClick={({ detail }) => {
                          if (detail.id === 'gravity') navigate(`/batches/${id}/modal/gravity`);
                          else if (detail.id === 'hydropi') setShowHydroPiModal(true);
                          else if (detail.id === 'taste') setShowTasteModal(true);
                          // else if (detail.id === 'excise') setShowExciseDutyModal(true);
                          else if (detail.id === 'abv') setFinalAbv();
                          else if (detail.id === 'loss') setShowLossModal(true);
                          else if (detail.id === 'agp' && batch?.recipe?.containers?.length) setShowAgpModal(true);
                          else if (detail.id === 'link-invoices') setShowModal(true);
                          else updateBatchStatus(detail.id as 'planned' | 'in-progress' | 'completed' | 'archived');
                        }}
                        items={[
                          {
                            text: 'Status',
                            items: [
                              {
                                disabled: batch?.status === 'planned',
                                text: 'Planned',
                                id: 'planned',
                              },
                              {
                                disabled: batch?.status === 'in-progress',
                                text: 'In progress',
                                id: 'in-progress',
                              },
                              {
                                disabled: batch?.status === 'completed',
                                text: 'Completed',
                                id: 'completed',
                              },
                              {
                                disabled: batch?.status === 'archived',
                                text: 'Archived',
                                id: 'archived',
                              },
                            ],
                          },
                          {
                            text: 'Sample',
                            items: [
                              { text: 'Gravity', id: 'gravity' },
                              { text: 'Taste', id: 'taste' },
                              { text: 'HydroPi Config', id: 'hydropi' },
                            ],
                          },

                          {
                            text: 'Audit tracking',
                            items: [
                              {
                                text: 'Loss',
                                id: 'loss',
                              },
                              {
                                text: `AGP${!batch?.recipe?.containers?.length ? ' (no containers)' : ''}`,
                                id: 'agp',
                                disabled: !batch?.recipe?.containers?.length,
                              },
                              ...(!batch?.abvFinal
                                ? [
                                    {
                                      text: 'Set final ABV (auto)',
                                      id: 'abv',
                                    },
                                  ]
                                : []),
                              {
                                text: `Excise duty${!batch?.abvFinal ? ' (no final ABV)' : ''}`,
                                id: 'excise',
                                disabled: !batch?.abvFinal,
                              },

                              {
                                text: 'Link to invoices',
                                id: 'link-invoices',
                              },
                            ],
                          },
                        ]}
                        ariaLabel="Sample batch"
                        variant="inline-icon"
                      />
                    )}
                  </>
                }
              >
                General information ({(batch?.recipeSlug?.charAt(0)?.toUpperCase() ?? '') + batch?.recipeSlug?.slice(1)}
                )
              </Header>
            }
          >
            <ColumnLayout columns={2} variant="text-grid">
              <FormField label="Batch name">
                {editMode ? (
                  <Input
                    value={batch?.name ?? ''}
                    onChange={({ detail }) =>
                      setBatch((prevBatch) => {
                        if (!prevBatch)
                          return {
                            ...initalBatch,
                            name: detail.value,
                          };
                        return { ...prevBatch, name: detail.value };
                      })
                    }
                  />
                ) : (
                  (batch?.name ?? 'N/A')
                )}
              </FormField>
              <FormField label="Recipe">
                {editMode ? (
                  <Select
                    disabled={id !== 'new'}
                    selectedOption={{
                      label: (batch?.recipe?.name ?? '') + ' - ' + (batch?.recipe?.style ?? ''),
                      value: batch?.recipe?.id ?? '',
                    }}
                    onChange={({ detail }) =>
                      setBatch((prevBatch) => {
                        if (!prevBatch)
                          return {
                            ...initalBatch,
                            recipeId: detail.selectedOption.value ?? '',
                            recipe: recipes.find((r) => r.id === detail.selectedOption.value),
                          };
                        return {
                          ...prevBatch,
                          recipeId: detail.selectedOption.value ?? '',
                          recipe: recipes.find((r) => r.id === detail.selectedOption.value),
                        };
                      })
                    }
                    options={
                      recipes.map((recipe) => ({
                        label: recipe.name + ' - ' + recipe.style,
                        value: recipe.id,
                      })) ?? []
                    }
                  />
                ) : (
                  (batch?.recipe?.name ?? '') + ' - ' + (batch?.recipe?.style ?? '')
                )}
              </FormField>
              {(tenantObject?.departments?.length ?? 0) > 0 && (
                <FormField label="Department">
                  {editMode ? (
                    <DepartmentSelector
                      local
                      fullName
                      departmentState={batch?.department ?? ''}
                      departmentSetState={(val) => {
                        setBatch((prevBatch) => {
                          if (!prevBatch)
                            return {
                              ...initalBatch,
                              department: val,
                            };
                          return { ...prevBatch, department: val };
                        });
                      }}
                    />
                  ) : (
                    (batch?.department ?? 'All')
                  )}
                </FormField>
              )}
              <FormField label="Slug" info="Product id">
                {editMode ? (
                  <Input
                    value={batch?.recipeSlug ?? ''}
                    onChange={({ detail }) =>
                      setBatch((prevBatch) => {
                        if (!prevBatch)
                          return {
                            ...initalBatch,
                            recipeSlug: detail.value,
                          };
                        return { ...prevBatch, recipeSlug: detail.value };
                      })
                    }
                  />
                ) : (
                  (batch?.recipeSlug ?? 'N/A')
                )}
              </FormField>
              <FormField label="Status">
                <SpaceBetween size="m" direction="horizontal">
                  {editMode ? (
                    <Select
                      selectedOption={{
                        label: (
                          (batch?.status ?? 'planned').charAt(0).toUpperCase() + (batch?.status ?? 'planned').slice(1)
                        ).replace('-', ' '),
                        value: batch?.status ?? 'planned',
                      }}
                      onChange={({ detail }) =>
                        setBatch((prevBatch) => {
                          if (!prevBatch)
                            return {
                              ...initalBatch,
                              status:
                                (detail.selectedOption?.value as
                                  | 'planned'
                                  | 'in-progress'
                                  | 'completed'
                                  | 'archived') ?? 'planned',
                            };
                          return {
                            ...prevBatch,
                            status:
                              (detail.selectedOption.value as 'planned' | 'in-progress' | 'completed' | 'archived') ??
                              'planned',
                          };
                        })
                      }
                      options={[
                        { label: 'Planned', value: 'planned' },
                        { label: 'In progress', value: 'in-progress' },
                        { label: 'Completed', value: 'completed' },
                        { label: 'Archived', value: 'archived' },
                      ]}
                    />
                  ) : (
                    <Badge
                      color={
                        batch?.status === 'in-progress' ? 'blue' : batch?.status === 'completed' ? 'green' : 'grey'
                      }
                    >
                      {(
                        (batch?.status ?? 'planned').charAt(0).toUpperCase() + (batch?.status ?? 'planned').slice(1)
                      ).replace('-', ' ')}
                    </Badge>
                  )}
                </SpaceBetween>
              </FormField>
              <FormField label="Quantity (L)" info="Related to the recipe ingredients size. (Q/R)*I">
                {editMode ? (
                  <Input value={quantity} onChange={({ detail }) => setQuantity(detail.value)} />
                ) : (
                  (batch?.quantity ?? 'N/A')
                )}
              </FormField>
              <FormField label="Volume (L)" info="What goes into your fermenter.">
                {editMode ? (
                  <Input value={volume} onChange={({ detail }) => setVolume(detail.value)} />
                ) : (
                  (batch?.volume ?? 'N/A')
                )}
              </FormField>
              <FormField label="Yield (L)" info="The amount that is drinkable.">
                {editMode ? (
                  <Input value={yieldAmount} onChange={({ detail }) => setYieldAmount(detail.value)} />
                ) : (
                  (batch?.yield ?? 'N/A')
                )}
              </FormField>

              <FormField label="Expected yield (L)" info="The amount that is expected to be drinkable.">
                {editMode ? (
                  <Input value={expectedYield} onChange={({ detail }) => setExpectedYield(detail.value)} />
                ) : (
                  (batch?.expectedYield ?? 'N/A')
                )}
              </FormField>

              <FormField label="Final ABV (Not Adjusted) (%)">
                {unadjustedAbv !== undefined ? `${unadjustedAbv.toFixed(2)} %` : 'Not available'}
              </FormField>

              <FormField
                label={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Final ABV (Adjusted) (%)
                    {adjustedAbv !== undefined && batch?.ingredientsConfirmed && (
                      <Popover
                        position="right"
                        size="large"
                        triggerType="custom"
                        content={
                          <div style={{ maxWidth: 400 }}>
                            <div>
                              <strong>Original OG:</strong> {originalOg?.toFixed(3)}
                            </div>
                            <div>
                              <strong>Original FG:</strong> {originalFg?.toFixed(3)}
                            </div>
                            <div style={{ marginTop: '8px', fontWeight: 'bold' }}>Ingredients contributing sugar:</div>
                            <ul style={{ paddingLeft: '20px' }}>
                              {adjustmentIngredients.map((ing, idx) => (
                                <li key={idx}>
                                  {ing.name}, Quantity: {ing.quantity} {ing.unit}, SugarContent:{' '}
                                  {ing.sugarContentPer100g}% → {Math.round(ing.sugarGrams / 0.9)}g potential,{' '}
                                  {Math.round(ing.sugarGrams)}g used (90%)
                                </li>
                              ))}
                            </ul>
                            <div style={{ marginTop: '8px' }}>
                              <strong>Total sugar added:</strong> {Math.round(totalSugarGrams ?? 0)}g
                            </div>
                            <div>
                              <strong>Volume:</strong> {volumeLiters?.toFixed(1)} L
                            </div>
                            <div>
                              <strong>Extra Gravity Points:</strong> {extraGP?.toFixed(3)} GP
                            </div>
                            <div>
                              <strong>Adjusted OG:</strong> {adjustedOg?.toFixed(3)}
                            </div>
                            <div>
                              <strong>Final Adjusted ABV:</strong> {adjustedAbv?.toFixed(2)}%
                            </div>
                            <div
                              style={{
                                fontSize: '12px',
                                fontStyle: 'italic',
                                marginTop: '12px',
                                color: '#555',
                              }}
                            >
                              Note: Fruit sugar contributions assume 90% extraction efficiency to preserve flavor
                              integrity.
                            </div>
                          </div>
                        }
                      >
                        <Button variant="inline-icon" iconName="status-info" />
                      </Popover>
                    )}
                  </div>
                }
              >
                {adjustedAbv !== undefined ? `${adjustedAbv.toFixed(2)} %` : 'Not available'}
              </FormField>

              <FormField label="Start date" constraintText="Use YYYY/MM/DD format.">
                {editMode ? (
                  <DatePicker
                    value={startDate}
                    onChange={({ detail }) => {
                      setStartDate(detail.value);
                      setBatch((prevBatch) => {
                        if (!prevBatch)
                          return {
                            ...initalBatch,
                            startDate: new Date(detail.value),
                          };
                        return {
                          ...prevBatch,
                          startDate: new Date(detail.value),
                        };
                      });
                    }}
                  />
                ) : startDate ? (
                  new Date(startDate)?.toLocaleDateString()
                ) : (
                  'No date yet'
                )}
              </FormField>
              <FormField label="End date" constraintText="Use YYYY/MM/DD format.">
                {editMode ? (
                  <DatePicker
                    value={endDate}
                    onChange={({ detail }) => {
                      setEndDate(detail.value);
                      setBatch((prevBatch) => {
                        if (!prevBatch)
                          return {
                            ...initalBatch,
                            endDate: new Date(detail.value),
                          };
                        return {
                          ...prevBatch,
                          endDate: new Date(detail.value),
                        };
                      });
                    }}
                  />
                ) : endDate ? (
                  new Date(endDate)?.toLocaleDateString()
                ) : batch?.endDate ? (
                  toDate(batch?.endDate).toLocaleDateString()
                ) : (
                  (batch?.startDate
                    ? (addDaysBySteps(toDate(batch.startDate), batch?.recipe?.steps ?? [])?.toLocaleDateString() ??
                      'No date yet')
                    : 'No date yet') + ' (expected)'
                )}
              </FormField>
              <FormField label="Notes" info="Add any additional notes for the batch.">
                {editMode ? (
                  <Input value={notes} onChange={({ detail }) => setNotes(detail.value)} />
                ) : (
                  (batch?.notes ?? 'N/A')
                )}
              </FormField>
              <FormField label="Score" info="Rate the batch, 1 to 5.">
                {editMode ? (
                  <Slider
                    onChange={({ detail }) => setScore(detail.value)}
                    value={score}
                    valueFormatter={(value) => `${value / 100}`}
                    step={5}
                    max={500}
                    min={0}
                    referenceValues={[100, 200, 300, 400, 500]}
                  />
                ) : score < 0 ? (
                  ''
                ) : (
                  <>
                    {renderStars(score)} {(score ?? -1) / 100}
                  </>
                )}
              </FormField>
            </ColumnLayout>
          </Container>
        </div>

        {batch && id !== 'new' && <Ingredients batch={batch} batchSize={batchSize} />}

        {batch?.recipe?.containers && batch.recipe.containers.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <Container header={<Header variant="h2">Packaging</Header>}>
              <SpaceBetween size="s">
                {batch.recipe.containers.map((c, idx) => (
                  <Box key={c.containerItem.id + '-' + idx} padding="s">
                    <SpaceBetween size="xs" direction="horizontal">
                      <Badge>Container</Badge>
                      <strong>{c.containerItem.name}</strong>
                      <span>{c.sizeL} L</span>
                    </SpaceBetween>
                    <Box margin={{ top: 'xs' }}>
                      {!c.components?.length ? (
                        <i>No per-container components</i>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {c.components.map((pc, i2) => (
                            <li key={pc.item.id + '-' + i2}>
                              {pc.amountPerContainer} {pc.unitOverride ?? pc.item.unit} × {pc.item.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </Box>
                  </Box>
                ))}
              </SpaceBetween>
            </Container>
          </div>
        )}

        {batch?.exciseDuty && <ExciseDuty exciseDuty={batch.exciseDuty} />}

        {batch && (batch?.currentStepIndex ?? 0) < (batch?.recipe?.steps?.length ?? 99) && (
          <CurrentStep
            batch={batch}
            stepPrevious={stepPrevious}
            stepStart={stepStart}
            stepComplete={stepComplete}
            stepNext={stepNext}
          />
        )}

        {batch && <Steps batch={batch} setEditStep={setEditStep} />}

        {batch && <Cost batch={batch} />}

        {batch?.hydroPi && <HydroPi config={batch.hydroPi} />}

        {gravitySamples && gravitySamples.length > 0 && <Gravity gravitySamples={gravitySamples} />}

        {batch?.tasteSamples && <Taste batch={batch} />}

        {batch && id !== 'new' && <BatchGraphView tenant={tenant} batchId={batch.id} batchName={batch.name} />}
      </ContentLayout>

      <Modal
        visible={editStep > -1}
        onDismiss={() => setEditStep(-1)}
        header={batch?.recipe?.steps[editStep]?.name ?? ''}
        footer={
          <div style={{ float: 'right' }}>
            <SpaceBetween size="m" direction="horizontal">
              <Button onClick={() => setEditStep(-1)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setEditStep(-1);
                  saveBatch();
                }}
              >
                Save
              </Button>
            </SpaceBetween>
          </div>
        }
      >
        <SpaceBetween size="m" direction="horizontal">
          <FormField label="Start date">
            <DatePicker
              value={
                batch?.recipe?.steps[editStep]?.start
                  ? (toDate(batch?.recipe?.steps[editStep]?.start ?? new Date())
                      ?.toISOString()
                      .split('T')[0] ?? '')
                  : ''
              }
              onChange={({ detail }) =>
                setBatch((prevBatch) => {
                  if (!prevBatch) return { ...initalBatch };
                  const newRecipe = prevBatch.recipe;
                  if (!newRecipe) return { ...initalBatch };
                  const newStep = newRecipe.steps[editStep];
                  if (!newStep) return { ...initalBatch };
                  newStep.start = new Date(detail.value);
                  return { ...prevBatch, recipe: newRecipe };
                })
              }
            />
          </FormField>
          <FormField label="End date">
            <DatePicker
              value={
                batch?.recipe?.steps[editStep]?.end
                  ? (toDate(batch?.recipe?.steps[editStep]?.end ?? new Date())
                      ?.toISOString()
                      .split('T')[0] ?? '')
                  : ''
              }
              onChange={({ detail }) =>
                setBatch((prevBatch) => {
                  if (!prevBatch) return { ...initalBatch };
                  const newRecipe = prevBatch.recipe;
                  if (!newRecipe) return { ...initalBatch };
                  const newStep = newRecipe.steps[editStep];
                  if (!newStep) return { ...initalBatch };
                  newStep.end = new Date(detail.value);
                  return { ...prevBatch, recipe: newRecipe };
                })
              }
            />
          </FormField>
        </SpaceBetween>
      </Modal>
      <Modal
        visible={!!id && id !== 'new' && modal === 'gravity'}
        onDismiss={() => navigate('/batches/' + id)}
        header="Gravity sample"
      >
        {batch?.recipe ? (
          <GravitySample recipe={batch?.recipe} step={batch?.currentStepIndex ?? 0} />
        ) : (
          'No recipe found'
        )}
      </Modal>
      <Modal visible={showHydroPiModal} onDismiss={() => setShowHydroPiModal(false)} header="HydroPi Config">
        <HydroPiConfigModal batchId={id} onClose={() => setShowHydroPiModal(false)} />
      </Modal>
      {batch && (
        <Modal
          visible={showExciseDutyModal}
          onDismiss={() => setShowExciseDutyModal(false)}
          header="Excise duty information"
        >
          <ExciseDutyModal
            batch={batch}
            batchAgp={batchAgp}
            abvFinal={batch.abvFinal ?? -1}
            onClose={() => {
              setShowExciseDutyModal(false);
              if (id) fetchBatch(id, tenant).then(setBatch);
            }}
          />
        </Modal>
      )}
      {id && id !== 'new' && (
        <AgpModal
          batchId={id}
          visible={showAgpModal}
          setVisible={setShowAgpModal}
          slug={batch?.recipeSlug}
          containers={batch?.recipe?.containers}
        />
      )}

      <TasteSampleModal
        visible={showTasteModal}
        onClose={() => setShowTasteModal(false)}
        onSave={handleAddTasteSample}
      />
      <UpdateQuantityModal visible={finishModal} onClose={() => setFinishModal(false)} />
      {batch && <LossModal batch={batch} visible={showLossModal} setVisible={setShowLossModal} />}

      {id && id !== 'new' && (
        <GridLinkModal
          showModal={showModal}
          setShowModal={setShowModal}
          fromId={id}
          fromType="batch"
          toTypes={['invoice']}
        />
      )}

      {
        <ConfirmIngredientsModal
          showIngredientsModal={showIngredientModal}
          setShowIngredientsModal={setShowIngredientModal}
          confirmIngredients={confirmIngredients}
          editedIngredients={editedIngredients}
          setEditedIngredients={setEditedIngredients}
        />
      }
    </>
  );
};

export default BatchPage;
